import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Image, Briefcase, FileText, MessageSquare, ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { canAccess, user } = useAuth();
  const [stats, setStats] = useState({
    gallery: 0,
    jobs: 0,
    applications: 0,
    messages: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      // Only fetch stats for sections the user has permission to access
      const requests = [];
      const statKeys = [];

      if (canAccess('gallery')) {
        requests.push(axios.get(`${API}/admin/gallery`));
        statKeys.push('gallery');
      }
      if (canAccess('jobs')) {
        requests.push(axios.get(`${API}/admin/jobs`));
        statKeys.push('jobs');
      }
      if (canAccess('applications')) {
        requests.push(axios.get(`${API}/admin/applications`));
        statKeys.push('applications');
      }
      // Messages are accessible to all authenticated users
      requests.push(axios.get(`${API}/admin/messages`));
      statKeys.push('messages');

      const results = await Promise.all(requests);

      const newStats = { gallery: 0, jobs: 0, applications: 0, messages: 0 };
      results.forEach((res, idx) => {
        newStats[statKeys[idx]] = res.data.length;
      });

      setStats(newStats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Define all possible stats with their permission requirements
  const allQuickStats = [
    {
      title: 'Gallery Images',
      value: stats.gallery,
      icon: <Image className="w-8 h-8" />,
      color: 'bg-blue-500',
      link: '/admin/gallery',
      section: 'gallery'
    },
    {
      title: 'Active Jobs',
      value: stats.jobs,
      icon: <Briefcase className="w-8 h-8" />,
      color: 'bg-green-500',
      link: '/admin/jobs',
      section: 'jobs'
    },
    {
      title: 'Applications',
      value: stats.applications,
      icon: <FileText className="w-8 h-8" />,
      color: 'bg-amber-500',
      link: '/admin/applications',
      section: 'applications'
    },
    {
      title: 'Messages',
      value: stats.messages,
      icon: <MessageSquare className="w-8 h-8" />,
      color: 'bg-purple-500',
      link: '/admin/messages',
      section: null // Always visible
    }
  ];

  // Filter stats based on permissions
  const quickStats = allQuickStats.filter(stat => 
    stat.section === null || canAccess(stat.section)
  );

  const getRoleName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'hr_admin': return 'HR Admin';
      default: return 'Editor';
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-8 text-white">
        <h2 className="text-3xl font-bold mb-2">Welcome back, {user?.full_name || user?.username || 'Admin'}!</h2>
        <p className="text-amber-100">
          {user?.role === 'hr_admin' 
            ? 'Manage job listings and applications from this dashboard'
            : user?.role === 'editor'
            ? 'Manage gallery and content from this dashboard'
            : 'Manage your Lehigh HSS website content from this dashboard'}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickStats.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow duration-300 cursor-pointer border-2 hover:border-amber-300">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg text-white`}>
                    {stat.icon}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {canAccess('gallery') && (
              <Link to="/admin/gallery">
                <Button className="w-full justify-between bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200">
                  Add Gallery Images
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {canAccess('jobs') && (
              <Link to="/admin/jobs">
                <Button className="w-full justify-between bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200">
                  Create Job Listing
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {canAccess('applications') && (
              <Link to="/admin/applications">
                <Button className="w-full justify-between bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200">
                  Review Applications
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
            {canAccess('settings') && (
              <Link to="/admin/settings">
                <Button className="w-full justify-between bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200">
                  Update Company Info
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Website Status</span>
              <span className="flex items-center gap-2 text-green-600 font-medium">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                Live
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Company Email</span>
              <span className="text-gray-900 font-medium">jobs@lhss-email.com</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Contact Email</span>
              <span className="text-gray-900 font-medium">info@lehigh-hss.com</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Help Section */}
      <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2">
        <CardContent className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            Use the navigation menu on the left to manage different sections of your website.
            All changes are saved automatically to the database.
          </p>
          <div className="flex gap-4">
            <Button variant="outline" className="border-2">
              View Documentation
            </Button>
            <Button className="bg-amber-500 hover:bg-amber-600">
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;